import React, { useState, useEffect, useMemo } from "react";
import { Button, Table, Spinner, Badge } from "react-bootstrap";
import { useTable, usePagination, useGlobalFilter, useSortBy } from "react-table";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useDispatch } from "react-redux";
import { Fn_FillListData, Fn_DeleteData } from "../../store/Functions";
import { useNavigate } from "react-router-dom";
import { MapPin, Plus, Edit2, Trash2 } from "react-feather";

const PageList_AreaZoneMaster = () => {
  const [gridData, setGridData] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const API_URL = `${API_WEB_URLS.MASTER}/0/token/AreaZoneMaster`;

  const loadData = () => {
    setLoading(true);
    Fn_FillListData(dispatch, setGridData, "gridData", API_URL + "/Id/0").finally(() =>
      setLoading(false)
    );
  };

  useEffect(() => {
    loadData();
  }, [dispatch, API_URL]);

  const data = useMemo(() => gridData || [], [gridData]);

  const columns = useMemo(
    () => [
      {
        Header: "Zone Name",
        accessor: "ZoneName",
        Cell: ({ row, value }) => {
          const type = row.original.ZoneType;
          const isAllowed = type === "ALLOWED";
          return (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", display: "flex", alignItems: "center", gap: 6 }}>
                <span
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: isAllowed ? "#22c55e" : "#ef4444",
                  }}
                />
                {value || "-"}
              </div>
              {row.original.BlockedReason && (
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2, fontStyle: "italic" }}>
                  &quot;{row.original.BlockedReason}&quot;
                </div>
              )}
            </div>
          );
        },
      },
      {
        Header: "Zone Type",
        accessor: "ZoneType",
        Cell: ({ value }) => {
          const isAllowed = value === "ALLOWED";
          return (
            <Badge
              bg={isAllowed ? "success" : "danger"}
              style={{ fontSize: 11, padding: "4px 8px", textTransform: "uppercase" }}
            >
              {isAllowed ? "Allowed (Serviceable)" : "Blocked (Restricted)"}
            </Badge>
          );
        },
      },
      {
        Header: "City",
        accessor: "CityName",
        Cell: ({ value }) => <span style={{ fontSize: 12, color: "#334155" }}>{value || "All Cities"}</span>,
      },
      {
        Header: "Vehicle Restriction",
        accessor: "VehicleTypeName",
        Cell: ({ value }) => (
          <span style={{ fontSize: 11, color: value ? "#0369a1" : "#64748b" }}>
            {value ? `Restricted to: ${value}` : "All Vehicles"}
          </span>
        ),
      },
      {
        Header: "Boundary Points",
        id: "pointsCount",
        Cell: ({ row }) => {
          let count = 0;
          try {
            const parsed = JSON.parse(row.original.CoordinatesJson || "[]");
            count = Array.isArray(parsed) ? parsed.length : 0;
          } catch (e) {
            count = 0;
          }
          return (
            <Badge bg="secondary" style={{ fontSize: 11 }}>
              {count} GPS Points
            </Badge>
          );
        },
      },
      {
        Header: "Status",
        accessor: "IsActive",
        Cell: ({ value }) => (
          <Badge bg={value ? "success" : "secondary"} style={{ fontSize: 10 }}>
            {value ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        id: "actions",
        Header: "Action",
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: 6 }}>
            <Button
              size="sm"
              variant="outline-primary"
              className="py-0 px-2 d-flex align-items-center gap-1"
              style={{ fontSize: 11, height: 26 }}
              onClick={() =>
                navigate("/AddAreaZoneMaster", {
                  state: { Id: row.original.Id },
                })
              }
            >
              <Edit2 size={12} /> Edit
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              className="py-0 px-2 d-flex align-items-center gap-1"
              style={{ fontSize: 11, height: 26 }}
              onClick={() => {
                if (window.confirm("Are you sure you want to delete this zone?")) {
                  Fn_DeleteData(dispatch, row.original.Id, API_URL, API_URL + "/Id/0").then(
                    (r) => r && r.success && loadData()
                  );
                }
              }}
            >
              <Trash2 size={12} /> Del
            </Button>
          </div>
        ),
      },
    ],
    [dispatch, navigate, API_URL]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 15, sortBy: [{ id: "ZoneType", desc: false }] },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  if (loading) {
    return (
      <div
        style={{
          padding: 20,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 180,
        }}
      >
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div style={{ padding: "8px 12px", maxWidth: 1200, margin: "0 auto" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
          paddingBottom: 6,
          borderBottom: "1px solid #dee2e6",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <MapPin size={18} color="#E87B2D" />
            <span style={{ fontSize: 16, fontWeight: 700, color: "#333" }}>
              Geofence &amp; Service Zones Master
            </span>
          </div>
          <p style={{ margin: 0, fontSize: 11, color: "#666" }}>
            Draw and manage Serviceable boundaries (Green) and Blocked no-entry zones (Red) on the interactive map.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="text"
            placeholder="Search zones..."
            value={globalFilter || ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="form-control form-control-sm"
            style={{ width: 160, height: 28, fontSize: 12 }}
          />
          <Button
            size="sm"
            variant="primary"
            className="py-0 px-2 d-flex align-items-center gap-1"
            style={{ fontSize: 12, height: 28 }}
            onClick={() => navigate("/AddAreaZoneMaster", { state: { Id: 0 } })}
          >
            <Plus size={14} /> Add New Zone
          </Button>
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", marginBottom: 6 }}>
        <Table
          {...getTableProps()}
          size="sm"
          bordered
          hover
          style={{ fontSize: 12, minWidth: 780, background: "#fff" }}
        >
          <thead>
            {headerGroups.map((headerGroup, i) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                {headerGroup.headers.map((col, j) => (
                  <th
                    {...col.getHeaderProps(col.getSortByToggleProps())}
                    key={j}
                    style={{
                      padding: "6px 8px",
                      fontSize: 11,
                      fontWeight: 600,
                      backgroundColor: "#E87B2D",
                      color: "#fff",
                    }}
                  >
                    {col.render("Header")}
                    {col.isSorted && (
                      <span style={{ marginLeft: 3 }}>{col.isSortedDesc ? "▼" : "▲"}</span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4 text-muted" style={{ fontSize: 13 }}>
                  No geofence zones found. Click <strong>+ Add New Zone</strong> to draw one on the map.
                </td>
              </tr>
            ) : (
              page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={i}>
                    {row.cells.map((cell, j) => (
                      <td
                        {...cell.getCellProps()}
                        key={j}
                        style={{ padding: "6px 8px", verticalAlign: "middle" }}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Button
            size="sm"
            variant="outline-secondary"
            className="py-0 px-2"
            style={{ fontSize: 11 }}
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
          >
            «
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            className="py-0 px-2"
            style={{ fontSize: 11 }}
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            ‹
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            className="py-0 px-2"
            style={{ fontSize: 11 }}
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            ›
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            className="py-0 px-2"
            style={{ fontSize: 11 }}
            onClick={() => gotoPage(pageOptions.length - 1)}
            disabled={!canNextPage}
          >
            »
          </Button>
          <span style={{ color: "#555" }}>
            Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length || 1}</strong>
          </span>
        </div>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          style={{ height: 26, fontSize: 11, padding: "0 6px" }}
          className="form-select form-select-sm"
        >
          {[10, 15, 25, 50].map((ps) => (
            <option key={ps} value={ps}>
              Show {ps}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default PageList_AreaZoneMaster;
